import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { tokens } from "../../Common/designTokens";
import { useSessionConfigFormStore } from "../../Store/sessionConfigFormStore";
import { inputStyle } from "../OrderEntry/styles";
import { useAuth } from "../../Context/AuthProvider";
import { fetchVenues, fetchCustomerInfo, fetchModuleGroups } from "../../http/api";

const SessionConfigForm = ({ mode = "add", initialData = null }) => {
  const { token } = useAuth();
  const { formData, setFormData, fieldErrors, clearFieldError } =
    useSessionConfigFormStore();

  // State for dropdown options
  const [groupOptions, setGroupOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [venueOptions, setVenueOptions] = useState([]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!token) return;

      try {
        // Fetch all dropdown data in parallel
        const [venuesResponse, customersResponse, groupsResponse] = await Promise.all([
          fetchVenues(token, 0, 100, "venueMstId", "ASC"),
          fetchCustomerInfo(token, 0, 100, "custId", "ASC"),
          fetchModuleGroups(token, 0, 100, "groupMasterId", "ASC"),
        ]);

        // Process venues
        if (venuesResponse?.success && venuesResponse?.data?.content) {
          setVenueOptions(
            venuesResponse.data.content.map((venue) => ({
              id: venue.venueId,
              name: venue.venueName,
            }))
          );
        }

        // Process customers
        if (customersResponse?.success && customersResponse?.data?.content) {
          setCustomerOptions(
            customersResponse.data.content.map((customer) => ({
              id: customer.custId,
              name: customer.customerName,
            }))
          );
        }

        // Process module groups
        if (groupsResponse?.success && groupsResponse?.data?.content) {
          setGroupOptions(
            groupsResponse.data.content.map((group) => ({
              id: group.groupMasterId,
              name: group.groupName,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, [token]);

  // Load initial data in edit mode
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        fixCustomerMappingId: initialData.fixCustomerMappingId,
        custId: initialData.custId || "",
        groupMasterId: initialData.groupMasterId || "",
        venueId: initialData.venueId || "",
        connectionType: initialData.connectionType || "RFQ",
        senderCompId: initialData.senderCompId || "",
        targetCompId: initialData.targetCompId || "",
        targetHost: initialData.targetHost || "",
        targetPort: initialData.targetPort || "",
        startTime: initialData.startTime || "",
        endTime: initialData.endTime || "",
        isActive: initialData.isActive !== undefined ? initialData.isActive : 1,
        venueName: initialData.venueName || "",
        customerName: initialData.customerName || "",
        groupName: initialData.groupName || "",
      });
    }
  }, [mode, initialData, setFormData]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const updates = { [field]: value };
    
    // Also store the corresponding name for dropdowns
    if (field === 'venueId') {
      const selectedVenue = venueOptions.find(v => v.id === value);
      updates.venueName = selectedVenue?.name || '';
    } else if (field === 'custId') {
      const selectedCustomer = customerOptions.find(c => c.id === value);
      updates.customerName = selectedCustomer?.name || '';
    } else if (field === 'groupMasterId') {
      const selectedGroup = groupOptions.find(g => g.id === value);
      updates.groupName = selectedGroup?.name || '';
    }
    
    setFormData(updates);
    clearFieldError(field);
  };

  const isFieldEditable = (field) => {
    if (mode === "add") return true;
    // In edit mode, only these fields are editable
    return ["targetHost", "targetPort", "startTime", "endTime"].includes(field);
  };

  // Validate IP address
  const validateIP = (ip) => {
    const ipRegex =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  // Validate port number
  const validatePort = (port) => {
    const portNum = parseInt(port, 10);
    return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
  };

  return (
    <Box
      sx={{
        padding: "24px",
        overflowY: "auto",
        maxHeight: "calc(95vh - 200px)",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "24px",
        }}
      >
        {/* Group Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="Group"
              value={formData.groupMasterId}
              onChange={handleChange("groupMasterId")}
              disabled={!isFieldEditable("groupMasterId")}
              sx={inputStyle}
              error={fieldErrors.groupMasterId}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="">
                <em>Select Group</em>
              </MenuItem>
              {groupOptions.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>

        {/* Customer Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="Customer"
              value={formData.custId}
              onChange={handleChange("custId")}
              disabled={!isFieldEditable("custId")}
              sx={inputStyle}
              error={fieldErrors.custId}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="">
                <em>Select Customer</em>
              </MenuItem>
              {customerOptions.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>

        {/* Venue Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="Venue"
              value={formData.venueId}
              onChange={handleChange("venueId")}
              disabled={!isFieldEditable("venueId")}
              sx={inputStyle}
              error={fieldErrors.venueId}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="">
                <em>Select Venue</em>
              </MenuItem>
              {venueOptions.map((venue) => (
                <MenuItem key={venue.id} value={venue.id}>
                  {venue.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>

        {/* Connection Type Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="Connection Type"
              value={formData.connectionType}
              onChange={handleChange("connectionType")}
              disabled={mode === "edit"}
              sx={inputStyle}
              error={fieldErrors.connectionType}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="RFQ">RFQ</MenuItem>
              <MenuItem value="STP">STP</MenuItem>
              <MenuItem value="MD">MD</MenuItem>

            </TextField>
          </FormControl>
        </Box>

        {/* Sender Comp ID */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Sender Comp ID"
            value={formData.senderCompId}
            onChange={handleChange("senderCompId")}
            disabled={!isFieldEditable("senderCompId")}
            inputProps={{ maxLength: 50 }}
            sx={inputStyle}
            error={
              fieldErrors.senderCompId ||
              (formData.senderCompId && formData.senderCompId.length > 50)
            }
            helperText={
              formData.senderCompId && formData.senderCompId.length > 50
                ? "Maximum 50 characters allowed"
                : ""
            }
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>

        {/* Target Comp ID */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Target Comp ID"
            value={formData.targetCompId}
            onChange={handleChange("targetCompId")}
            disabled={!isFieldEditable("targetCompId")}
            inputProps={{ maxLength: 50 }}
            sx={inputStyle}
            error={
              fieldErrors.targetCompId ||
              (formData.targetCompId && formData.targetCompId.length > 50)
            }
            helperText={
              formData.targetCompId && formData.targetCompId.length > 50
                ? "Maximum 50 characters allowed"
                : ""
            }
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>

        {/* Target Host */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Target Host (IP Address)"
            value={formData.targetHost}
            onChange={handleChange("targetHost")}
            disabled={!isFieldEditable("targetHost")}
            sx={inputStyle}
            error={
              fieldErrors.targetHost ||
              (formData.targetHost && !validateIP(formData.targetHost))
            }
            helperText={
              formData.targetHost && !validateIP(formData.targetHost)
                ? "Invalid IP address format"
                : ""
            }
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>

        {/* Target Port */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Target Port"
            value={formData.targetPort}
            onChange={handleChange("targetPort")}
            disabled={!isFieldEditable("targetPort")}
            type="number"
            inputProps={{ min: 1, max: 65535 }}
            sx={inputStyle}
            error={
              fieldErrors.targetPort ||
              (formData.targetPort && !validatePort(formData.targetPort))
            }
            helperText={
              formData.targetPort && !validatePort(formData.targetPort)
                ? "Port must be between 1 and 65535"
                : ""
            }
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>

        {/* Start Time */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Start Time"
            type="time"
            value={formData.startTime}
            onChange={handleChange("startTime")}
            disabled={!isFieldEditable("startTime")}
            sx={inputStyle}
            error={fieldErrors.startTime}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>

        {/* End Time */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="End Time"
            type="time"
            value={formData.endTime}
            onChange={handleChange("endTime")}
            disabled={!isFieldEditable("endTime")}
            sx={inputStyle}
            error={fieldErrors.endTime}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SessionConfigForm;
